import { type FC, type FormEvent, type ReactElement, useCallback, useState } from 'react';

import { fetchSessionBalance } from '../../../api/fetchers/sessionBalance/fetcher';
import { useRedirectToLobbyCallbackConnected } from '../../../hooks/useRedirectToLobbyCallbackConnected';
import { Logger } from '../../../logger/Logger';
import { L10nText } from '../../../services/uiTranslations/reactBindings/L10nText';
import { fetchUserInfoAction } from '../../../store/apiActions/fetchUserInfoAction';
import { useCoreDispatch } from '../../../store/CoreStore'; // ToDo: Should be exported as separate file
import { useConfigSlice } from '../../../store/slices/config/selectors';
import { configActions } from '../../../store/slices/config/slice';
import { useGameStateSlice } from '../../../store/slices/game/selectors';
import { popupActions } from '../../../store/slices/popup/slice';
import { ButtonVariant } from '../../controls/button/Button';
import { CurrencyConnected } from '../../currency';
import { DescriptionList } from '../../descriptionList/DescriptionList';
import { FormGrid } from '../../formGrid/FormGrid';
import { CurrencyInputConnected } from '../../inputs/currencyInput/currencyInputConnected';
import { PopupType } from '../../popup/types';

import { BlockingModalActionButton, BlockingModalContent } from './BlockingModalContent';

export const SessionBalanceConnected: FC = (): ReactElement => {
  const JSESSIONID = useConfigSlice.JSESSIONID();
  const dispatch = useCoreDispatch();
  const balance = useGameStateSlice.getDeductibleBalance();
  const userId = useConfigSlice.playerConfig()?.userId as string;
  const [isLoading, setIsLoading] = useState(false);
  const baseUrl = useConfigSlice.baseUrl() as string;
  const redirectToLobby = useRedirectToLobbyCallbackConnected();

  const sessionBalanceLimit = 1000; // FIXME I should not be hardcoded AND most likely I should be currency multiplier dependent
  const timeStamp = Date.now();

  const onSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      const form = event.target as HTMLFormElement;
      event.preventDefault();
      const isValid = form?.reportValidity();
      if (isValid) {
        setIsLoading(true);
        const { sessionBalance } = form;

        const body = {
          chosenBalance: sessionBalance.value,
          userId,
        };

        if (JSESSIONID && baseUrl) {
          fetchSessionBalance({
            url: `${baseUrl}/api/ui/rgsChoosenBalance`,
            ck: timeStamp.toString(),
            JSESSIONID: JSESSIONID,
            body,
          })
            .then((data) => {
              dispatch(fetchUserInfoAction({ JSESSIONID, force: true, baseUrl, essential: true }));
              dispatch(popupActions.dismissPopup(PopupType.SessionBalance));
              if (data.errorCode === '110') {
                dispatch(popupActions.pushPopup(PopupType.SessionBalanceError));
              } else {
                dispatch(configActions.sessionBalanceWasSet());
                dispatch(configActions.setAaamsSessions(data));
              }
            })
            .catch((error) => {
              Logger.logFailure('sessionBalance', {
                additional: error?.name ?? '',
              });
              dispatch(popupActions.dismissPopup(PopupType.SessionBalance));
              dispatch(popupActions.pushPopup(PopupType.GenericError));
            });
        } else {
          dispatch(popupActions.pushPopup(PopupType.GenericError));
        }
      }
    },
    [JSESSIONID, baseUrl, dispatch, timeStamp, userId]
  );

  const action: BlockingModalActionButton[] = [
    {
      tk: 'EXIT',
      type: ButtonVariant.MAIN,
      disabled: isLoading,
      click: (): void => {
        redirectToLobby();
      },
      buttonAttributes: {
        type: 'button',
      },
    },
    {
      tk: 'OK',
      type: ButtonVariant.ACCENT,
      loading: isLoading,
      disabled: isLoading,
      buttonAttributes: {
        type: 'submit',
      },
    },
  ];

  return (
    <form onSubmit={onSubmit}>
      <BlockingModalContent
        titleTk="AMOUNT_TO_PLAY"
        messageComponent={
          <FormGrid>
            <DescriptionList
              items={[
                {
                  title: <L10nText tk="AVAILABLE_BALANCE" />,
                  description: <CurrencyConnected value={balance} />,
                },
              ]}
            />
            <CurrencyInputConnected
              data-testid="session-balance-input"
              name="sessionBalance"
              initialValue={Math.min(sessionBalanceLimit, balance).toString()}
              min={0}
              max={Math.min(sessionBalanceLimit, balance)}
              errorMsg="THE_MAX_LIMIT_IS_1000"
              required
              label={<L10nText tk="SESSION_BALANCE" />}
            />
          </FormGrid>
        }
        actions={action}
      />
    </form>
  );
};
